import gsap from 'gsap';
import { Interaction } from 'three.interaction';
import GLBench from 'gl-bench/dist/gl-bench';
import { prepareInfrastructure } from './init/PrepareInfrastructure';
import GradientBackground from './objects/GradientBackground';
import { UNIT } from '../../constants/size';
import { COLOR_SCHEMES } from '../../constants/colors';

const THREE = require('three');

class ThreeShell {

    _isMouseUVChanged = false;

    _isMouseUV = new THREE.Vector2(0, 0);

    constructor() {
        this._zProgress = 0;
        this._scrollWeight = 0;
        this._camDistance = UNIT.camDistance;
        this._scheme = 'default';
        this.schemeChanged = false;

        this._construct();
    }

    _construct() {
        //
        // 1. Add Renderer, scenes and default cameras
        const mainCamParams = {
            far: UNIT.camFar
        };
        const bgCamParams = {
            far: 100
        };
        const interactionIsOn = true;
        const monitorGPUandCPU = false;
        const mainInfrastructure = prepareInfrastructure(
            null,
            mainCamParams,
            window.innerWidth,
            window.innerHeight,
            false,
            false,
            false,
            false
        );
        const hasRenderer = mainInfrastructure.renderer;
        const bgInfrastructure = hasRenderer
            ? prepareInfrastructure(mainInfrastructure.renderer, bgCamParams)
            : null;

        if (hasRenderer) {
            //
            // 2. Add Gradient Background
            const bgObject3D = new GradientBackground();
            const setConfigurationForBg = (bgIfs) => {
                bgIfs.camera.position.z = 0.006 * UNIT.zSize;
            };

            setConfigurationForBg(bgInfrastructure);
            bgInfrastructure.scene.add(bgObject3D);

            //
            // 3. Add root group to the main scene
            const { scene } = mainInfrastructure;
            const root3D = new THREE.Group();

            scene.add(root3D);

            //
            // 4. Add interaction to the main scene
            if (interactionIsOn) {
                const { renderer, camera } = mainInfrastructure;
                const interaction = new Interaction(renderer, scene, camera);

                // IMPORTANT! Need style correction to enable touch devices content scroll
                // touch-action: auto;
                interaction.renderer.domElement.style.touchAction = 'auto';

                // Layers:
                // 0 - static layers. Default
                // 1 - interactive layers
                camera.layers.enable(0);
                camera.layers.enable(1);
                interaction.raycaster.layers.disable(0);
                interaction.raycaster.layers.enable(1);

                this.interaction = interaction;
            }

            // GPU /CPU monitor
            if (monitorGPUandCPU) {
                const { renderer } = mainInfrastructure;
                const bench = new GLBench(renderer.getContext());
                const draw = (now) => {
                    bench.begin();
                    // monitored code
                    bench.end();
                    bench.nextFrame(now);
                };

                renderer.setAnimationLoop((now) => draw(now));
            }

            //
            // Store references 1:
            //
            this._bgObject3D = bgObject3D;
            this._root3D = root3D;
        }

        //
        // Store references 0:
        //
        this._infrastructuresRenderSequence = mainInfrastructure.renderer ? [bgInfrastructure, mainInfrastructure] : [];
        this._bgInfrastructure = bgInfrastructure;
        this._mainInfrastructure = mainInfrastructure;

        this._mainInfrastructure.raycaster = this.raycaster;

        //
        // 4. Update color scheme
        this._updateScheme();
    }

    start() {
        this._startRotateCam();
    }

    _startRotateCam() {
        this._addMoveListeners(['mousemove']);

        document.body.addEventListener('mouseleave', () => {
            this._changeMouseUV(0.55);
        });
    }

    _addMoveListeners(events) {
        events.forEach((eventType) => {
            document.body.addEventListener(eventType, (evt) => {
                if (!evt) return;

                const { innerWidth, innerHeight } = window;
                const { clientX, clientY } = evt;
                const relX = clientX / innerWidth;
                const relY = clientY / innerHeight;

                this._changeMouseUV(0.85, 2 * (relX - 0.5), 2 * (relY - 0.5));
            });
        });
    }

    _addTouchListeners(events) {
        events.forEach((eventType) => {
            document.body.addEventListener(eventType, (evt) => {
                if (!evt) return;

                const { touches } = evt;

                if (touches.length < 1) return;

                const { clientX, clientY } = touches[0];
                const { innerWidth, innerHeight } = window;

                const relX = clientX / innerWidth;
                const relY = clientY / innerHeight;

                this._changeMouseUV(0.85, 2 * (relX - 0.5), 2 * (relY - 0.5));
            });
        });
    }

    _changeMouseUV(duration, x = 0, y = 0) {
        gsap.killTweensOf(this._isMouseUV);
        gsap.to(this._isMouseUV, {
            x,
            y,
            duration,
            ease: 'power2.out',
            onUpdate: () => {
                this._isMouseUVChanged = true;
            }
        });
    }

    render() {
        this._infrastructuresRenderSequence.forEach((ifs) => ifs.render());
    }

    invalidate() {
        this._initiated = true; // console.log(this._zProgress, this._scrollWeight, this._zProgress * this._scrollWeight * UNIT.zSize);

        const { camera, scene } = this._mainInfrastructure;

        if (!camera) return;

        if (this._isMouseUVChanged) {
            const { x, y } = this._isMouseUV;

            camera.rotation.y = -x * Math.PI * 0.03;
            camera.rotation.x = -y * Math.PI * 0.02;

            this._isMouseUVChanged = false;
        }

        camera.position.z = this._camDistance - this._zProgress * this._scrollWeight * UNIT.zSize;

        scene.updateMatrixWorld();
    }

    resize() {
        const { renderer, camera } = this._mainInfrastructure;

        if (!renderer) return;

        const winW = window.innerWidth;
        const winH = window.innerHeight;

        const setAspectAnUpdate = (cam) => {
            cam.aspect = winW / winH;
            cam.updateProjectionMatrix();
        };

        //
        // MAIN:
        const pixelRatio = renderer.getPixelRatio();

        // - camera
        let fov = 35;

        if (winW < winH) {
            fov = (67 * winH) / Math.min(winW * 1.5, winH);
        }

        camera.fov = fov;
        setAspectAnUpdate(camera);

        //
        // BG:

        // - camera
        setAspectAnUpdate(this._bgInfrastructure.camera);

        // - gradient size
        this._bgObject3D.viewportWidth = winW * pixelRatio;
        this._bgObject3D.viewportHeight = winH * pixelRatio;

        renderer.setSize(winW, winH);
    }

    add(object3D) {
        if (!this.hasRenderer) return null;

        return this._root3D.add(object3D);
    }

    remove(object3D) {
        if (!this.hasRenderer) return null;

        return this._root3D.remove(object3D);
    }

    set progress(value) {
        this._zProgress = value;
    }

    get progress() {
        return this._zProgress;
    }

    set scrollWeight(value) {
        this._scrollWeight = value;
    }

    get scrollWeight() {
        return this._scrollWeight;
    }

    get domElement() {
        const { renderer } = this._mainInfrastructure;

        if (!renderer) return null;

        return renderer.domElement;
    }

    get infrastructure() {
        return this._mainInfrastructure;
    }

    get bgInfrastructure() {
        return this._bgInfrastructure;
    }

    set scheme(value) {
        if (!this.hasRenderer) return;
        if (value === this._scheme || typeof value !== 'string') return;

        this._scheme = value;
        this._updateScheme();

        // Need to be reset outside
        this.schemeChanged = true;
    }

    get scheme() {
        return this._scheme;
    }

    get hasRenderer() {
        return !!this.infrastructure.renderer;
    }

    _updateScheme() {
        if (!this.hasRenderer) return;

        const schemeConfig = COLOR_SCHEMES[this._scheme];

        if (!schemeConfig) return;

        const { scene, camera } = this._mainInfrastructure;

        const { fog, bg } = schemeConfig;

        // Add Fog
        scene.fog = new THREE.Fog(fog, camera.near + (0.8 * (camera.far - camera.near)), camera.far);
        this._bgObject3D.setGradient(bg);
    }
}

export default ThreeShell;
